import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
} from '@mui/material';
import { StaticMessageBubbles, SystemPrompt } from '@mcn-platform/converse-lib';
import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import ExpandMore from '@mui/icons-material/ExpandMore';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Error from '@mui/icons-material/Error';
import HourglassEmpty from '@mui/icons-material/HourglassEmpty';
import { IPortal, useStore } from '@mcn-platform/models';
import ConversationStoreAdapter from '@mcn-platform/portal-lib/src/portal/clientAi/ConversationStoreAdapter';
import {
  buildClientAIRequestArguments,
  getShoppingAssistantSystemPromptOverride,
} from '@mcn-platform/converse-lib';

import { CLIENT_AI_MODEL_OPTIONS } from '../../constants/clientAIModelOptions';
import { SHOPPING_ASSISTANT_PROMPT } from '../../constants/systemPromptDefaults';

type PromptRow = {
  id: string;
  prompt: string;
  runs: number;
  raw?: Record<string, unknown>;
  edited?: string[];
};

export type TestPromptsConfig = {
  prompts: Array<{ text: string; runs: number }>;
};

const defaultPromptRows = (): PromptRow[] => [
  { id: String(Date.now()), prompt: '', runs: 1 },
];

function testPromptsToRows(config: TestPromptsConfig | null | undefined, preserveUnknown = false): PromptRow[] {
  const list = config?.prompts;
  if (!Array.isArray(list) || list.length === 0) return defaultPromptRows();
  return list.map((p, i) => ({
    ...(preserveUnknown ? { raw: { ...p } } : {}),
    id: `loaded-${i}`,
    prompt: typeof p.text === 'string' ? p.text : '',
    runs: typeof p.runs === 'number' && p.runs >= 1 && p.runs <= 10 ? p.runs : 1,
  }));
}

type ResultStatus = 'pending' | 'running' | 'success' | 'error';

export type SearchCriteria = {
  query?: string;
  objectTypes?: string[];
  productOptions?: Record<string, string>;
  productTypes?: string[];
  externalIDs?: string[];
};

/** One search run (one tool invocation) – criteria, refs, and duration. */
export type SearchRun = {
  searchCriteria?: SearchCriteria;
  itemReferences?: Record<string, unknown>;
  searchDurationMs?: number;
};

type ResultItem = {
  id: string;
  prompt: string;
  runIndex: number;
  status: ResultStatus;
  text?: string;
  error?: string;
  /** Merged item references (for ItemReference / ItemList in message bubbles) */
  itemReferences?: Record<string, unknown>;
  /** One entry per search tool invocation; each gets its own table */
  searches?: SearchRun[];
};

const sha256Hex = async (message: string): Promise<string> => {
  const msgUint8 = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
};

const runOnePrompt = async (params: {
  portal: IPortal;
  clientAIUrl: string;
  organizationID: string;
  portalID: string;
  prompt: string;
  modelOverride?: string;
  /** When true, BedrockConverse uses HNSW-style pgvector queries (adminOptions.postFilter). */
  postFilter?: boolean;
  /** Same source as useConversation / Shopping Assistant widget */
  customerContext: unknown;
  userID?: string;
  location: string;
  clientAIUseDualSearch: boolean | undefined;
  onUpdate: (update: Partial<ResultItem>) => void;
  onSystemPrompt?: (systemPrompt: string, modelID?: string) => void;
}): Promise<void> => {
  const {
    portal,
    clientAIUrl,
    organizationID,
    portalID,
    prompt,
    modelOverride,
    postFilter,
    customerContext,
    userID,
    location,
    clientAIUseDualSearch,
    onUpdate,
    onSystemPrompt,
  } = params;
  onUpdate({ status: 'running' });
  const sessionID = `prompt-test-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const adminOptions: Record<string, unknown> = {
    systemPromptOverride: getShoppingAssistantSystemPromptOverride(portal, SHOPPING_ASSISTANT_PROMPT),
    returnSearchCriteria: true,
    returnSystemPrompt: true,
  };
  if (modelOverride) adminOptions.modelOverride = modelOverride;
  const requestBody = {
    messages: [
      { role: 'user' as const, content: prompt },
    ],
    arguments: buildClientAIRequestArguments({
      conversationOptions: {
        conversationType: 'shoppingAssistant',
        layoutMode: 'ai',
      },
      organizationID,
      portalID,
      sessionID,
      userID,
      location,
      customerContext,
      portalClientAIUseDualSearch: clientAIUseDualSearch,
      adminOptions,
      postFilter: postFilter ? true : undefined,
    }),
  };
  const bodyString = JSON.stringify(requestBody);
  const digestHex = await sha256Hex(bodyString);

  try {
    const res = await fetch(clientAIUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Amz-Content-Sha256': digestHex,
      },
      body: bodyString,
    });

    if (!res.ok) {
      const errorText = await res.text();
      onUpdate({ status: 'error', error: `${res.status}: ${errorText}` });
      return;
    }

    const reader = res.body?.getReader();
    if (!reader) {
      onUpdate({ status: 'error', error: 'No response body' });
      return;
    }

    const decoder = new TextDecoder();
    let fullText = '';
    let buffer = '';
    let sawError = false;

    let collectedItemReferences: Record<string, unknown> = {};
    const collectedSearches: SearchRun[] = [];
    const processLine = (line: string): void => {
      const trimmed = line.trim();
      if (!trimmed) return;
      const colonIndex = trimmed.indexOf(':');
      if (colonIndex === -1) return;
      const code = trimmed.slice(0, colonIndex);
      const jsonValue = trimmed.slice(colonIndex + 1);
      try {
        if (code === '0') {
          const part = JSON.parse(jsonValue);
          if (typeof part === 'string') fullText += part;
        } else if (code === '3') {
          const part = JSON.parse(jsonValue);
          onUpdate({
            status: 'error',
            error: typeof part === 'string' ? part : String(part),
          });
          sawError = true;
        } else if (code === '2') {
          // Only "data" parts (code 2). Skip code 8 (message_annotations): backend duplicates
          // search metadata there for the chat UI; same payload as append — would double-count searches.
          const part = JSON.parse(jsonValue);
          const arr = Array.isArray(part) ? part : [part];
          // eslint-disable-next-line no-restricted-syntax
          for (const item of arr) {
            if (item && typeof item === 'object') {
              if ('systemPrompt' in item && typeof item.systemPrompt === 'string' && onSystemPrompt) {
                onSystemPrompt(item.systemPrompt, typeof item.modelID === 'string' ? item.modelID : undefined);
              }
              const hasRefs = 'itemReferences' in item && item.itemReferences;
              const hasCriteria = 'searchCriteria' in item && item.searchCriteria
                && typeof item.searchCriteria === 'object';
              const durationMs = typeof item.searchDurationMs === 'number' ? item.searchDurationMs : undefined;
              if (hasRefs || hasCriteria || durationMs !== undefined) {
                if (hasRefs) {
                  const refs = item.itemReferences as Record<string, unknown>;
                  collectedItemReferences = { ...collectedItemReferences, ...refs };
                }
                collectedSearches.push({
                  ...(hasCriteria && {
                    searchCriteria: item.searchCriteria as SearchCriteria,
                  }),
                  ...(hasRefs && {
                    itemReferences: item.itemReferences as Record<string, unknown>,
                  }),
                  ...(durationMs !== undefined && { searchDurationMs: durationMs }),
                });
              }
            }
          }
        }
      } catch {
        // Skip unparseable lines
      }
    };

    const readAllChunks = async (
      acc: Uint8Array[] = [],
    ): Promise<Uint8Array[]> => {
      const result = await reader.read();
      if (result.value) acc.push(result.value);
      if (result.done) return acc;
      return readAllChunks(acc);
    };

    const chunks = await readAllChunks();
    const totalLen = chunks.reduce((sum, c) => sum + c.length, 0);
    const combined = new Uint8Array(totalLen);
    let offset = 0;
    chunks.forEach((c) => {
      combined.set(c, offset);
      offset += c.length;
    });
    buffer = decoder.decode(combined);
    const allLines = buffer.split('\n');
    const last = allLines.pop() ?? '';
    allLines.forEach((line) => processLine(line));
    if (last.trim()) processLine(last);

    if (sawError) return;
    const hasRefs = Object.keys(collectedItemReferences).length > 0;
    onUpdate({
      status: 'success',
      text: fullText || '(No text in response)',
      ...(hasRefs && { itemReferences: collectedItemReferences }),
      ...(collectedSearches.length > 0 && { searches: collectedSearches }),
    });
  } catch (err: unknown) {
    const message = err instanceof Error
      ? (err as Error).message
      : 'Unknown error';
    onUpdate({ status: 'error', error: message });
  }
};

const truncate = (s: string, maxLen: number): string => (s.length <= maxLen ? s : `${s.slice(0, maxLen)}…`);

const getStr = (o: Record<string, unknown>, ...keys: string[]): string => {
  const found = keys.map((k) => o[k]).find((v) => typeof v === 'string' && (v as string).length > 0);
  return typeof found === 'string' ? found : '';
};

const refTitle = (ref: unknown): string => {
  if (!ref || typeof ref !== 'object') return '';
  const o = ref as Record<string, unknown>;
  const t = getStr(o, 'title', 'Title', 'name');
  if (t) return t;
  const meta = o.metadata as Record<string, unknown> | undefined;
  return meta ? getStr(meta, 'title', 'Title', 'name') : '';
};

const refUrl = (ref: unknown): string | undefined => {
  if (!ref || typeof ref !== 'object') return undefined;
  const o = ref as Record<string, unknown>;
  const u = getStr(o, 'url', 'Url');
  if (u) return u;
  const meta = o.metadata as Record<string, unknown> | undefined;
  const mu = meta ? getStr(meta, 'url', 'Url') : '';
  return mu || undefined;
};

/** UUID (8-4-4-4-12 hex) then handle; capture handle to show as title when missing. */
// eslint-disable-next-line max-len
const UUID_HANDLE_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-(.+)$/i;

/** Human-readable label from key; for product keys show only handle (e.g. "Topo Traverse Mens"). */
const keyToDisplayLabel = (key: string): string => {
  const afterSlash = key.includes('/') ? key.replace(/^[^/]+\//, '') : key;
  const match = afterSlash.match(UUID_HANDLE_REGEX);
  const handle = match ? match[1] : afterSlash;
  const withSpaces = handle.replace(/-/g, ' ');
  return withSpaces.replace(/\b\w/g, (c) => c.toUpperCase());
};

const refLabel = (key: string, ref: unknown): string => {
  const title = refTitle(ref);
  return title ? `${key} — ${title}` : key;
};

/** Renders title then (key as link to product page) opening in new tab. */
const RefDisplay: React.FC<{
  refKey: string;
  ref: unknown;
  baseUrl?: string | null;
  /** When true, link to admin product editor instead of portal (for compare table in admin). */
  adminProductLinks?: boolean;
}> = ({
  refKey,
  ref,
  baseUrl,
  adminProductLinks = false,
}) => {
  const title = refTitle(ref);
  let url: string | undefined;
  if (adminProductLinks && refKey.startsWith('product/')) {
    const path = refKey.replace(/^product\//, '');
    url = `${window.location.origin}/app/product-editor/${path}`;
  } else {
    url = refUrl(ref);
    if (!url && baseUrl && refKey.startsWith('product/')) {
      const path = refKey.replace(/^product\//, '');
      url = `${baseUrl.replace(/\/$/, '')}/${path}`;
    }
  }
  const displayTitle = title || keyToDisplayLabel(refKey) || refKey;
  if (url) {
    return (
      <Typography variant="body2" component="span">
        {displayTitle}
        {' '}
        (
        <Typography
          component="a"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          sx={{ color: 'primary.main', textDecoration: 'underline' }}
        >
          {refKey.includes('/') ? refKey.replace(/^[^/]+\//, '') : refKey}
        </Typography>
        )
      </Typography>
    );
  }
  return <Typography variant="body2" component="span">{refLabel(refKey, ref)}</Typography>;
};
RefDisplay.defaultProps = { baseUrl: undefined, adminProductLinks: false };

const SearchCriteriaDisplay: React.FC<{
  criteria: SearchCriteria;
  searchDurationMs?: number;
}> = ({ criteria, searchDurationMs }) => (
  <Box sx={{ mb: 1 }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
      Search criteria
    </Typography>
    <Typography variant="body2" component="span">
      Query:
      <strong>{criteria.query ?? '—'}</strong>
      {Array.isArray(criteria.objectTypes) && criteria.objectTypes.length > 0
        && ` · objectTypes: [${criteria.objectTypes.join(', ')}]`}
      {criteria.productOptions != null && Object.keys(criteria.productOptions).length > 0
        && ` · productOptions: ${JSON.stringify(criteria.productOptions)}`}
      {Array.isArray(criteria.productTypes) && criteria.productTypes.length > 0
        && ` · productTypes: [${criteria.productTypes.join(', ')}]`}
      {Array.isArray(criteria.externalIDs) && criteria.externalIDs.length > 0
        && ` · externalIDs: [${criteria.externalIDs.join(', ')}]`}
      {typeof searchDurationMs === 'number' && (
        <>
          {' · '}
          <Typography component="span" variant="body2" color="text.secondary">
            {searchDurationMs}
            {' '}
            ms
          </Typography>
        </>
      )}
    </Typography>
  </Box>
);
SearchCriteriaDisplay.defaultProps = { searchDurationMs: undefined };

/** Numbered table of item references from a search run. */
const SearchResultsTable: React.FC<{
  itemReferences?: Record<string, unknown>;
  baseUrl?: string | null;
  /** When true, ref links go to admin product/category etc. pages and open in new tab. */
  adminProductLinks?: boolean;
}> = ({ itemReferences, baseUrl, adminProductLinks = false }) => {
  const singleEntries = itemReferences ? Object.entries(itemReferences) : [];
  if (singleEntries.length === 0) return null;

  return (
    <TableContainer sx={{ mt: 0.5 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, width: 48 }}>#</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Reference</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {singleEntries.map(([key, ref], i) => (
            <TableRow key={key}>
              <TableCell sx={{ verticalAlign: 'top', width: 48 }}>{i + 1}</TableCell>
              <TableCell sx={{ verticalAlign: 'top' }}>
                <RefDisplay refKey={key} ref={ref} baseUrl={baseUrl} adminProductLinks={adminProductLinks} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
SearchResultsTable.defaultProps = {
  itemReferences: undefined,
  baseUrl: undefined,
  adminProductLinks: false,
};

const StatusChip: React.FC<{ status: ResultStatus }> = ({ status }) => {
  if (status === 'running' || status === 'pending') {
    const isRunning = status === 'running';
    let icon: React.ReactNode;
    if (isRunning) {
      icon = <CircularProgress size={14} color="inherit" />;
    } else {
      icon = <HourglassEmpty />;
    }
    return (
      <Chip
        size="small"
        icon={icon}
        label={isRunning ? 'Running' : 'Pending'}
        sx={{
          ml: 1,
          bgcolor: 'grey.200',
          color: 'grey.700',
          '& .MuiChip-icon': { overflow: 'visible' },
        }}
      />
    );
  }
  if (status === 'success') {
    return (
      <Chip
        size="small"
        icon={<CheckCircle />}
        label="Success"
        color="success"
        sx={{ ml: 1, bgcolor: 'success.main', color: 'success.contrastText' }}
      />
    );
  }
  return (
    <Chip
      size="small"
      icon={<Error />}
      label="Error"
      color="error"
      sx={{ ml: 1, bgcolor: 'error.main', color: 'error.contrastText' }}
    />
  );
};

type PromptTestingFieldProps = {
  portal: IPortal;
  configuration?: Record<string, any>;
  onConfigChange?: (field: string, value: any) => void;
};
const PromptTestingFieldInner: React.FC<PromptTestingFieldProps> = ({ portal, configuration, onConfigChange }) => {
  const store = useStore();
  const adapter = useMemo(
    () => new ConversationStoreAdapter(store),
    [store],
  );
  const {
    clientAIUrl,
    currentOrganizationID: organizationID,
    currentPortalID: portalID,
  } = adapter;

  const [prompts, setPrompts] = useState<PromptRow[]>(
    () => testPromptsToRows(configuration?.testPrompts || portal?.clientAIConfig?.testPrompts, Boolean(onConfigChange)),
  );
  const [results, setResults] = useState<ResultItem[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [modelOverride, setModelOverride] = useState('');
  const [postFilter, setPostFilter] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState<string | null>(null);
  const [systemPromptModelID, setSystemPromptModelID] = useState<string | undefined>(undefined);
  const didInitialSync = useRef(false);

  // Load prompts from portal when switching portal or when portal loads
  // (omit testPrompts from deps so we don't reload when we sync edits to portal)
  useEffect(() => {
    didInitialSync.current = false;
    setPrompts(testPromptsToRows(configuration?.testPrompts || portal?.clientAIConfig?.testPrompts, Boolean(onConfigChange)));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only load on portal id/syncState
  }, [portal?.id, portal?.syncState, configuration]);

  // Sync prompt changes to portal (in-memory); main Save persists
  useEffect(() => {
    if (!didInitialSync.current) {
      didInitialSync.current = true;
      return;
    }
    const testPrompts: TestPromptsConfig = {
      ...(onConfigChange ? configuration?.testPrompts : {}),
      prompts: prompts.map((p) => p.raw ? {
        ...p.raw,
        ...(p.edited?.includes('prompt') ? { text: p.prompt } : {}),
        ...(p.edited?.includes('runs') ? { runs: p.runs } : {}),
      } as { text: string; runs: number } : { text: p.prompt, runs: p.runs }),
    };
    // Only write back if testPrompts differ from portal (avoids marking form dirty on load)
    const fromPortal = configuration?.testPrompts ?? portal?.clientAIConfig?.testPrompts ?? { prompts: [] };
    if (JSON.stringify(testPrompts) === JSON.stringify(fromPortal)) return;
    // Don't write default empty state when portal had no test prompts
    const isEmptyDefault = prompts.length <= 1 && prompts.every((p) => !p.prompt.trim());
    if (!portal?.clientAIConfig?.testPrompts && isEmptyDefault) return;
    onConfigChange?.('testPrompts', testPrompts);
    const config = { ...(portal.clientAIConfig || {}), testPrompts };
    portal.updateFields(['clientAIConfig'], { clientAIConfig: config } as never);
  }, [prompts, portal, configuration, onConfigChange]);

  const addPrompt = useCallback(() => {
    setPrompts((prev) => [
      ...prev,
      { id: String(Date.now()), prompt: '', runs: 1 },
    ]);
  }, []);

  const removePrompt = useCallback((id: string) => {
    setPrompts((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const updatePrompt = useCallback((id: string, updates: Partial<PromptRow>) => {
    setPrompts((prev) => prev.map((p) => (p.id === id ? { ...p, ...updates, ...(onConfigChange ? { edited: [...new Set([...(p.edited || []), ...Object.keys(updates)])] } : {}) } : p)));
  }, []);

  const updateResult = useCallback((id: string, update: Partial<ResultItem>) => {
    setResults((prev) => prev.map((r) => (r.id === id ? { ...r, ...update } : r)));
  }, []);

  const runAll = useCallback(async () => {
    const rows = prompts.filter((p) => p.prompt.trim());
    if (!rows.length || !clientAIUrl || !organizationID || !portalID) return;

    const flat: ResultItem[] = [];
    rows.forEach((row) => {
      const runCount = Math.max(
        1,
        Math.min(10, Math.floor(row.runs) || 1),
      );
      for (let r = 0; r < runCount; r += 1) {
        flat.push({
          id: `${row.id}-${r}`,
          prompt: row.prompt.trim(),
          runIndex: r + 1,
          status: 'pending',
        });
      }
    });

    setResults(flat);
    setIsRunning(true);

    await Promise.all(flat.map((item) => runOnePrompt({
      portal,
      clientAIUrl,
      organizationID,
      portalID,
      prompt: item.prompt,
      modelOverride: modelOverride || undefined,
      postFilter: postFilter || undefined,
      customerContext: adapter.customerContext,
      userID: adapter.currentUser?.id,
      location: typeof window !== 'undefined' ? window.location.href : '',
      clientAIUseDualSearch: portal.clientAIConfig?.clientAIUseDualSearch,
      onUpdate: (update) => updateResult(item.id, update),
      onSystemPrompt: (text, modelID) => {
        setSystemPrompt(text);
        if (modelID !== undefined) setSystemPromptModelID(modelID);
      },
    })));

    setIsRunning(false);
  }, [prompts, portal, clientAIUrl, organizationID, portalID, modelOverride, postFilter, adapter, updateResult]);

  const canRun = prompts.some((p) => p.prompt.trim())
    && !isRunning
    && !!clientAIUrl
    && !!organizationID
    && !!portalID;

  return (
    <Stack spacing={3}>
      <Typography variant="subtitle1" color="textSecondary">
        Add prompts and how many times to run each. Submit runs all in parallel
        and shows results in accordions.
      </Typography>

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <TextField
            select
            label="Model Override (for testing)"
            value={modelOverride}
            onChange={(e) => setModelOverride(e.target.value)}
            size="small"
          >
            {CLIENT_AI_MODEL_OPTIONS.map((option) => (
              <MenuItem key={option.value || 'default'} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </FormControl>
        <FormControlLabel
          control={(
            <Checkbox
              checked={postFilter}
              onChange={(e) => setPostFilter(e.target.checked)}
              size="small"
            />
          )}
          label="Post HNSW Filter"
        />
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block">
        Post HNSW Filter uses approximate index search for pgvector (faster on large catalogs).
      </Typography>

      {prompts.map((row) => (
        <Stack key={row.id} direction="row" spacing={1} alignItems="flex-start">
          <TextField
            label="Prompt"
            multiline
            minRows={2}
            value={row.prompt}
            onChange={(e) => updatePrompt(row.id, { prompt: e.target.value })}
            fullWidth
            size="small"
            placeholder="Enter a test prompt..."
          />
          <TextField
            label="Runs"
            type="number"
            value={row.runs}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              updatePrompt(row.id, {
                runs: Number.isNaN(v) ? 1 : Math.max(1, Math.min(10, v)),
              });
            }}
            inputProps={{ min: 1, max: 10 }}
            size="small"
            sx={{ width: 80 }}
          />
          <IconButton
            onClick={() => removePrompt(row.id)}
            disabled={prompts.length <= 1}
            aria-label="Remove prompt"
          >
            <Delete />
          </IconButton>
        </Stack>
      ))}

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button startIcon={<Add />} onClick={addPrompt} size="small">
          Add prompt
        </Button>
        <Button
          variant="contained"
          onClick={runAll}
          disabled={!canRun}
          size="medium"
        >
          {isRunning ? 'Running…' : 'Run all'}
        </Button>
      </Stack>

      {results.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Results
          </Typography>
          {systemPrompt != null && (
            <Box sx={{ mb: 2 }}>
              <SystemPrompt systemPrompt={systemPrompt} modelID={systemPromptModelID} />
            </Box>
          )}
          {results.map((r, index) => (
            <Accordion key={r.id} defaultExpanded={index === 0 || r.status === 'error'}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                  {truncate(r.prompt, 60)}
                  {` (run ${r.runIndex})`}
                </Typography>
                <StatusChip status={r.status} />
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  {r.status === 'success' && r.text !== undefined && (
                    <StaticMessageBubbles
                      prompt={r.prompt}
                      response={r.text}
                      itemReferences={r.itemReferences}
                      stylesheet={portal?.stylesheet}
                    />
                  )}
                  {r.status === 'success' && (r.searches?.length ?? 0) > 0 && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Search results
                      </Typography>
                      {r.searches!.map((search, idx) => {
                        const searchLabel = search.searchCriteria?.query ?? `run-${idx}`;
                        const boxKey = `search-${String(searchLabel).slice(0, 40)}-${idx}`;
                        return (
                          <Box key={boxKey} sx={{ mt: idx > 0 ? 2 : 0 }}>
                            {r.searches!.length > 1 && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mb: 0.5 }}
                              >
                                Search
                                {' '}
                                {idx + 1}
                              </Typography>
                            )}
                            {search.searchCriteria ? (
                              <SearchCriteriaDisplay
                                criteria={search.searchCriteria}
                                searchDurationMs={search.searchDurationMs}
                              />
                            ) : (
                              typeof search.searchDurationMs === 'number' && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  Search took
                                  {' '}
                                  {search.searchDurationMs}
                                  {' '}
                                  ms
                                </Typography>
                              )
                            )}
                            <SearchResultsTable
                              itemReferences={search.itemReferences}
                              baseUrl={portal?.clientAIBaseUrl}
                              adminProductLinks
                            />
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                  {r.status === 'error' && r.error && (
                    <Typography variant="body2" color="error">
                      {r.error}
                    </Typography>
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
    </Stack>
  );
};

const PromptTestingField: React.FC<PromptTestingFieldProps> = (props) => (
  <PromptTestingFieldInner {...props} />
);

export default PromptTestingField;
